import { useId, useRef, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Autocomplete } from '@base-ui/react/autocomplete'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'

import {
  type Ancestor,
  type CancelledInvitation,
} from '@/modules/hierarchy/api/hierarchy-api'
import { ApiError } from '@/lib/api'
import { joinIds } from '@/lib/utils'
import { Button } from '@/ui/button'
import { ConfirmDialog } from '@/ui/confirm-dialog'

import { CancelledInvitationList } from '../../cancelled-invitation-list'
import {
  moveEntityErrorMessage,
  useEntityAncestors,
  useMoveEntity,
  useMoveImpact,
} from '../../lib/entity-queries'
import { type EntityRef } from '../../lib/use-entity-recents'
import { useEntitySearchBox } from './use-entity-search-box'
import { useTreeKeyboard } from './use-tree-keyboard'
import {
  EntitySearchInput,
  EntitySearchResults,
  searchStatusText,
} from './entity-search-results'
import { EntityTreeNode } from './entity-tree-node'
import { pathChildMap } from './path-child'
import { sectionLabel } from './styles'

const moveEntitySchema = z.object({
  parent: z
    .object({ id: z.string().min(1), name: z.string().min(1) })
    .nullable()
    .refine((value) => value !== null, 'Select a parent'),
})

type MoveEntityInput = z.input<typeof moveEntitySchema>
type MoveEntityValues = z.output<typeof moveEntitySchema>

function ParentPicker({
  entityId,
  currentParentId,
  ancestors,
  root,
  value,
  onChange,
  onBlur,
  inputRef,
  invalid,
  describedBy,
}: {
  entityId: string
  currentParentId: string | null
  ancestors: Ancestor[]
  root: Ancestor
  value: EntityRef | null
  onChange: (value: EntityRef) => void
  onBlur: () => void
  inputRef: (element: HTMLInputElement | null) => void
  invalid: boolean
  describedBy?: string
}) {
  const pathChild = pathChildMap(ancestors)
  const {
    query,
    setQuery,
    isSearching,
    search,
    results: searchResults,
    pending: searchPending,
  } = useEntitySearchBox(root.entityId)
  const [toggled, setToggled] = useState<Set<string>>(new Set())
  const expanded = new Set(ancestors.map((ancestor) => ancestor.entityId))
  toggled.forEach((id) => {
    if (expanded.has(id)) expanded.delete(id)
    else expanded.add(id)
  })

  function toggleExpanded(id: string) {
    setToggled((previous) => {
      const next = new Set(previous)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const treeRef = useRef<HTMLDivElement>(null)
  const treeLabelId = useId()
  const tree = useTreeKeyboard({
    treeRef,
    onToggleExpanded: toggleExpanded,
    onSelect: onChange,
    fallbackActiveId: value?.id ?? root.entityId,
  })
  const focused = useRef(false)

  function registerInput(element: HTMLInputElement | null) {
    inputRef(element)
    if (element && !focused.current) {
      focused.current = true
      element.focus()
    }
  }

  return (
    <Autocomplete.Root
      mode="none"
      open={isSearching && search.error == null}
      onOpenChange={() => {}}
      value={query}
      onValueChange={setQuery}
    >
      <EntitySearchInput
        query={query}
        onValueChange={setQuery}
        ref={registerInput}
        onBlur={onBlur}
        ariaInvalid={invalid}
        ariaDescribedBy={describedBy}
      />
      <Autocomplete.Status className="text-muted-foreground px-2 text-xs empty:sr-only">
        {searchStatusText(
          isSearching,
          searchPending,
          search.error != null,
          searchResults.length,
        )}
      </Autocomplete.Status>

      {isSearching ? (
        <EntitySearchResults
          results={searchResults}
          error={search.error}
          hasNextPage={search.data?.pageInfo.hasNextPage ?? false}
          selectedId={value?.id ?? null}
          disabledId={currentParentId}
          blockedRootId={entityId}
          onSelect={onChange}
          onRetry={() => void search.refetch()}
        />
      ) : (
        <div className="flex max-h-72 flex-col gap-0.5 overflow-y-auto">
          <p id={treeLabelId} className={sectionLabel}>
            New parent
          </p>
          <div
            ref={treeRef}
            role="tree"
            tabIndex={-1}
            aria-labelledby={treeLabelId}
            onClick={tree.onClick}
            onKeyDown={tree.onKeyDown}
            onFocus={tree.onFocus}
            className="flex flex-col gap-0.5"
          >
            <EntityTreeNode
              entityId={root.entityId}
              name={root.name}
              hasChildren={root.hasChildren}
              depth={0}
              parentId={null}
              expanded={expanded}
              selectedId={value?.id ?? null}
              activeId={tree.activeId}
              pathChild={pathChild}
              registerNode={tree.registerNode}
              disabledId={currentParentId}
              blockedRootId={entityId}
            />
          </div>
        </div>
      )}
    </Autocomplete.Root>
  )
}

export function MoveEntityForm({
  entityId,
  entityName,
  currentParentId,
  onCancel,
  onSuccess,
}: {
  entityId: string
  entityName: string
  currentParentId: string | null
  onCancel: () => void
  onSuccess: () => void
}) {
  const ancestorsQuery = useEntityAncestors(entityId)
  const ancestors = ancestorsQuery.data?.items ?? []
  const root = ancestors[0] ?? null
  const selectionErrorId = useId()
  const requestErrorId = useId()
  const invitationsLabelId = useId()
  const submitRef = useRef<HTMLButtonElement>(null)
  const [confirming, setConfirming] = useState<{
    parent: EntityRef
    invitations: CancelledInvitation[]
  } | null>(null)
  const impact = useMoveImpact()
  const move = useMoveEntity()
  const cancelled = confirming?.invitations ?? []
  const { control, handleSubmit } = useForm<
    MoveEntityInput,
    unknown,
    MoveEntityValues
  >({
    resolver: zodResolver(moveEntitySchema),
    defaultValues: { parent: null },
    mode: 'onSubmit',
  })
  const candidate = useWatch({ control, name: 'parent' })
  const pickerAvailable =
    !ancestorsQuery.isLoading &&
    ancestorsQuery.error == null &&
    root !== null &&
    root.entityId !== entityId

  function submit({ parent }: MoveEntityValues) {
    if (!parent) return
    impact.mutate(
      { entityId, newParentEntityId: parent.id },
      { onSuccess: (invitations) => setConfirming({ parent, invitations }) },
    )
  }

  function confirmMove() {
    if (!confirming) return
    const newParentEntityId = confirming.parent.id
    move.mutate(
      {
        entityId,
        newParentEntityId,
        expectedCancelledInvitationIds: confirming.invitations.map(
          (invitation) => invitation.invitationId,
        ),
      },
      {
        onSuccess: () => {
          setConfirming(null)
          onSuccess()
        },
        onError: (error) => {
          if (
            error instanceof ApiError &&
            error.code === 'PENDING_INVITATIONS_CHANGED'
          )
            impact.mutate(
              { entityId, newParentEntityId },
              {
                onSuccess: (invitations) =>
                  setConfirming(
                    (current) => current && { ...current, invitations },
                  ),
              },
            )
        },
      },
    )
  }

  const recheckStatus = !confirming
    ? ''
    : impact.isPending
      ? 'Checking pending invitations…'
      : impact.isError
        ? moveEntityErrorMessage(impact.error, entityName)
        : ''

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        if (impact.isPending) return
        impact.reset()
        void handleSubmit(submit)(event)
      }}
      className="flex flex-col gap-3"
    >
      <div className="flex flex-col gap-1">
        <p className="text-xs font-medium">Move entity</p>
        <p className="text-muted-foreground text-xs">
          Choose a new parent for{' '}
          <strong className="text-foreground font-semibold">
            {entityName}
          </strong>
          .
        </p>
      </div>

      {ancestorsQuery.isLoading ? (
        <p className="text-muted-foreground text-xs">Loading…</p>
      ) : ancestorsQuery.error ? (
        <p role="alert" className="text-destructive text-xs">
          {ancestorsQuery.error.message}
        </p>
      ) : root && root.entityId === entityId ? (
        <p className="text-muted-foreground text-xs">
          {entityName} is at the top of your hierarchy; there's no parent to
          move it to.
        </p>
      ) : root ? (
        <Controller
          control={control}
          name="parent"
          render={({ field, fieldState }) => (
            <>
              <ParentPicker
                entityId={entityId}
                currentParentId={currentParentId}
                ancestors={ancestors}
                root={root}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                inputRef={field.ref}
                invalid={fieldState.invalid}
                describedBy={joinIds(
                  fieldState.error && selectionErrorId,
                  impact.isError && !confirming && requestErrorId,
                )}
              />
              {fieldState.error && (
                <p
                  id={selectionErrorId}
                  role="alert"
                  className="text-destructive text-xs"
                >
                  {fieldState.error.message}
                </p>
              )}
            </>
          )}
        />
      ) : (
        <p className="text-muted-foreground text-xs">
          No hierarchy available to move within.
        </p>
      )}

      {impact.isError && !confirming && (
        <p
          id={requestErrorId}
          role="alert"
          className="text-destructive text-xs"
        >
          {moveEntityErrorMessage(impact.error, entityName)}
        </p>
      )}

      <div className="flex items-center justify-between gap-1.5">
        <output
          aria-live="polite"
          className="text-muted-foreground truncate text-xs"
        >
          {impact.isPending && !confirming
            ? 'Checking pending invitations…'
            : candidate
              ? `Into: ${candidate.name}`
              : 'Select a parent'}
        </output>
        <div className="flex gap-1.5">
          <Button
            type="button"
            variant="ghost"
            disabled={move.isPending}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            ref={submitRef}
            type="submit"
            disabled={move.isPending || !pickerAvailable}
          >
            {impact.isPending && !confirming ? 'Checking…' : 'Move'}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirming(null)
            move.reset()
            impact.reset()
          }
        }}
        title={`Move ${entityName} into ${confirming?.parent.name ?? 'the new parent'}?`}
        description={
          cancelled.length === 0 ? (
            'No pending invitations are affected.'
          ) : (
            <span id={invitationsLabelId}>
              These pending invitations will be cancelled:
            </span>
          )
        }
        details={
          <>
            {cancelled.length > 0 && (
              <CancelledInvitationList
                invitations={cancelled}
                labelledBy={invitationsLabelId}
              />
            )}
            <p
              aria-live="polite"
              className="text-muted-foreground text-xs empty:hidden"
            >
              {recheckStatus}
            </p>
          </>
        }
        confirmLabel="Move"
        pendingLabel="Moving…"
        confirmVariant={cancelled.length > 0 ? 'destructive' : 'default'}
        confirmDisabled={impact.isPending || impact.isError}
        mutation={move}
        onConfirm={confirmMove}
        finalFocus={submitRef}
        errorMessage={(error) => moveEntityErrorMessage(error, entityName)}
      />
    </form>
  )
}
